import React, { useState } from "react";
import { Box, Button, Chip, TextField } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Check, Lightbulb, Sparkles, X } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Surface } from "../../Common/Surface";
import { entryVariants, resolveTransition } from "../motion";
import type { ProposalPseudoMessage } from "../../../chat/types";

export interface ProposalCardProps {
  entry: ProposalPseudoMessage;
  /** Wired from useChatContext().chat.acceptProposal. May reject. */
  onAccept: (proposal_id: string) => Promise<void>;
  /** Wired from useChatContext().chat.rejectProposal. May reject. */
  onReject: (proposal_id: string, reason?: string) => Promise<void>;
}

interface FactPayload {
  category?: string;
  text?: string;
  evidence?: string;
}
interface SkillPayload {
  name?: string;
  description?: string;
  body?: string;
}

/** Single-line summary used in terminal states. Mirrors what the
 *  pending card shows in its primary text so users can scan history
 *  without re-expanding rejected cards. */
function summarize(entry: ProposalPseudoMessage): string {
  if (entry.kind === "fact") {
    const p = entry.payload as FactPayload;
    return p.text ?? "(fact proposal)";
  }
  const p = entry.payload as SkillPayload;
  return p.name ?? "(skill proposal)";
}

export function ProposalCard({ entry, onAccept, onReject }: ProposalCardProps) {
  const [busy, setBusy] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const reduce = useReducedMotion() ?? false;

  if (entry.status !== "pending") {
    return <TerminalCard entry={entry} />;
  }

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onAccept(entry.id);
    } catch (exc) {
      setBusy(false);
      setError(exc instanceof Error ? exc.message : "Failed to accept");
    }
  };

  const reject = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onReject(entry.id, reason.trim() || undefined);
    } catch (exc) {
      setBusy(false);
      setShowRejectInput(false);
      setError(exc instanceof Error ? exc.message : "Failed to reject");
    }
  };

  const Icon = entry.kind === "fact" ? Lightbulb : Sparkles;
  const headline = entry.kind === "fact" ? "Remember this?" : "Add this skill?";

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={entryVariants}
      transition={resolveTransition(reduce)}
    >
    <Surface
      level="mid"
      sx={{
        my: 1.5,
        mx: 4, // narrower than full-width bubbles so the card reads as a system-emitted artifact
        px: 2,
        py: 1.5,
        borderStyle: "dashed",
        borderWidth: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
      role="region"
      aria-label={`${entry.kind} proposal`}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Icon size={16} aria-hidden style={{ flexShrink: 0 }} />
        <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
          {headline}
        </Box>
        <Chip
          size="small"
          variant="outlined"
          label={entry.kind}
          sx={{ ml: "auto", textTransform: "capitalize" }}
        />
      </Box>

      {entry.kind === "fact" ? (
        <FactBody payload={entry.payload as FactPayload} />
      ) : (
        <SkillBody payload={entry.payload as SkillPayload} />
      )}

      {error && (
        <Box sx={{ color: "error.main", fontSize: 12 }}>{error}</Box>
      )}

      {showRejectInput ? (
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={1}
            maxRows={3}
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
          />
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              setShowRejectInput(false);
              setReason("");
            }}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={reject}
            disabled={busy}
          >
            Reject
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            size="small"
            color="inherit"
            startIcon={<X size={14} />}
            onClick={() => setShowRejectInput(true)}
            disabled={busy}
          >
            Reject
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Check size={14} />}
            onClick={accept}
            disabled={busy}
          >
            Accept
          </Button>
        </Box>
      )}
    </Surface>
    </motion.div>
  );
}

function FactBody({ payload }: { payload: FactPayload }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {payload.category && (
          <Chip
            size="small"
            label={payload.category}
            sx={{ fontFamily: "monospace", fontSize: 11 }}
          />
        )}
        <Box component="span" sx={{ fontSize: 14 }}>
          {payload.text}
        </Box>
      </Box>
      {payload.evidence && (
        <Box
          component="span"
          sx={{
            fontSize: 12,
            color: "text.secondary",
            fontStyle: "italic",
          }}
        >
          Because: {payload.evidence}
        </Box>
      )}
    </Box>
  );
}

function SkillBody({ payload }: { payload: SkillPayload }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
        {payload.name}
      </Box>
      {payload.description && (
        <Box component="span" sx={{ fontSize: 13, color: "text.secondary" }}>
          {payload.description}
        </Box>
      )}
      {payload.body && (
        <Box
          component="details"
          sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}
        >
          <Box
            component="summary"
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            Show full skill body
          </Box>
          <Box
            component="pre"
            sx={{
              mt: 0.5,
              p: 1,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              borderRadius: 1,
              background: (t) => alpha(t.palette.primary.main, 0.04),
              fontFamily: "monospace",
              fontSize: 11,
              maxHeight: 240,
              overflow: "auto",
            }}
          >
            {payload.body}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function TerminalCard({ entry }: { entry: ProposalPseudoMessage }) {
  const label =
    entry.status === "accepted"
      ? "Accepted"
      : entry.status === "rejected"
        ? "Rejected"
        : "Expired";
  const Icon =
    entry.status === "accepted" ? Check : entry.status === "rejected" ? X : null;
  const color =
    entry.status === "accepted"
      ? "success.main"
      : entry.status === "rejected"
        ? "text.secondary"
        : "text.disabled";
  return (
    <Box
      sx={{
        my: 0.5,
        mx: 4,
        px: 1.5,
        py: 0.5,
        fontSize: 12,
        color,
        display: "flex",
        gap: 0.75,
        alignItems: "center",
      }}
      role="status"
      aria-label={`${entry.kind} proposal ${entry.status}`}
    >
      {Icon && <Icon size={12} aria-hidden />}
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}:
      </Box>
      <Box
        component="span"
        sx={{
          textDecoration: entry.status === "rejected" ? "line-through" : "none",
          opacity: entry.status === "rejected" ? 0.7 : 1,
        }}
      >
        {summarize(entry)}
      </Box>
    </Box>
  );
}
